import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateSelectArg, EventClickArg, EventInput } from '@fullcalendar/core';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material';
import {
  AddBox as AddBoxIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  applyBreaksToWorkingPlan,
  businessDays,
  defaultBusinessLogicSettings,
  mergeBusinessLogicSettings,
  type BusinessDayKey,
  type BusinessLogicSettings,
} from '../../lib/businessLogic';
import { useTenant } from '../../providers/TenantProvider';
import { SettingsShell } from './SettingsShell';

function newId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function asDateInputValue(value: any) {
  const date =
    value?.toDate?.() instanceof Date
      ? value.toDate()
      : value instanceof Date
        ? value
        : typeof value === 'string'
          ? new Date(value)
          : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function asDate(value: any) {
  const date =
    value?.toDate?.() instanceof Date
      ? value.toDate()
      : value instanceof Date
        ? value
        : typeof value === 'string'
          ? new Date(value)
          : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function fromDateInputValue(value: string) {
  return Timestamp.fromDate(value ? new Date(value) : new Date());
}

export function BusinessLogicPage() {
  const { tenantId, activeTenant } = useTenant();
  const [settings, setSettings] = useState<BusinessLogicSettings>(() =>
    mergeBusinessLogicSettings(activeTenant?.businessLogic || defaultBusinessLogicSettings)
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const timezone = activeTenant?.defaultTimezone || 'America/New_York';
  const workingPlanWithBreaks = useMemo(
    () => applyBreaksToWorkingPlan(settings.workingPlan, settings.defaultBreaks),
    [settings.defaultBreaks, settings.workingPlan]
  );
  const blockedPeriodEvents = useMemo<EventInput[]>(
    () =>
      settings.blockedPeriods.map((period) => ({
        id: period.id,
        title: period.label || 'Blocked',
        start: asDate(period.startTime),
        end: asDate(period.endTime),
        color: '#d32f2f',
        extendedProps: { periodId: period.id },
      })),
    [settings.blockedPeriods]
  );

  useEffect(() => {
    let mounted = true;

    async function loadSettings() {
      if (!tenantId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
        if (!mounted) return;
        setSettings(mergeBusinessLogicSettings(tenantSnap.data()?.businessLogic));
      } catch (error) {
        console.error('Error loading business logic:', error);
        setMessage({ type: 'error', text: 'Failed to load business logic settings.' });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      mounted = false;
    };
  }, [tenantId]);

  const updateWorkingDay = (
    day: BusinessDayKey,
    patch: { isWorking?: boolean; start?: string; end?: string }
  ) => {
    setSettings((current) => {
      const previous = current.workingPlan[day] || {
        isWorking: true,
        start: '09:00',
        end: '17:00',
        breaks: [],
      };
      const isWorking = patch.isWorking ?? previous.isWorking;
      return {
        ...current,
        workingPlan: {
          ...current.workingPlan,
          [day]: isWorking
            ? {
                ...previous,
                isWorking,
                start: patch.start ?? previous.start ?? '09:00',
                end: patch.end ?? previous.end ?? '17:00',
              }
            : null,
        },
      };
    });
  };

  const addBreak = () => {
    setSettings((current) => ({
      ...current,
      defaultBreaks: [
        ...current.defaultBreaks,
        { id: newId('break'), day: 'monday', start: '12:00', end: '13:00' },
      ],
    }));
  };

  const updateBreak = (
    id: string,
    patch: Partial<{ day: BusinessDayKey; start: string; end: string }>
  ) => {
    setSettings((current) => ({
      ...current,
      defaultBreaks: current.defaultBreaks.map((breakPeriod) =>
        breakPeriod.id === id ? { ...breakPeriod, ...patch } : breakPeriod
      ),
    }));
  };

  const removeBreak = (id: string) => {
    setSettings((current) => ({
      ...current,
      defaultBreaks: current.defaultBreaks.filter((breakPeriod) => breakPeriod.id !== id),
    }));
  };

  const addBlockedPeriod = () => {
    const start = new Date();
    start.setHours(9, 0, 0, 0);
    const end = new Date(start);
    end.setHours(17, 0, 0, 0);

    setSettings((current) => ({
      ...current,
      blockedPeriods: [
        ...current.blockedPeriods,
        {
          id: newId('blocked'),
          label: 'Closed',
          startTime: Timestamp.fromDate(start),
          endTime: Timestamp.fromDate(end),
        },
      ],
    }));
  };

  const updateBlockedPeriod = (
    id: string,
    patch: Partial<{ label: string; startTime: Timestamp; endTime: Timestamp }>
  ) => {
    setSettings((current) => ({
      ...current,
      blockedPeriods: current.blockedPeriods.map((period) =>
        period.id === id ? { ...period, ...patch } : period
      ),
    }));
  };

  const removeBlockedPeriod = (id: string) => {
    setSettings((current) => ({
      ...current,
      blockedPeriods: current.blockedPeriods.filter((period) => period.id !== id),
    }));
  };

  const addBlockedPeriodFromSelection = (selection: DateSelectArg) => {
    const start = selection.start;
    const end =
      selection.end && selection.end.getTime() > selection.start.getTime()
        ? selection.end
        : new Date(selection.start.getTime() + 60 * 60 * 1000);

    setSettings((current) => ({
      ...current,
      blockedPeriods: [
        ...current.blockedPeriods,
        {
          id: newId('blocked'),
          label: 'Closed',
          startTime: Timestamp.fromDate(start),
          endTime: Timestamp.fromDate(end),
        },
      ],
    }));
  };

  const scrollToBlockedPeriod = (event: EventClickArg) => {
    const periodId = event.event.extendedProps.periodId as string | undefined;
    if (!periodId) return;
    document.getElementById(`blocked-period-${periodId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const saveSettings = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'tenants', tenantId), {
        businessLogic: {
          workingPlan: workingPlanWithBreaks,
          defaultBreaks: settings.defaultBreaks,
          blockedPeriods: settings.blockedPeriods,
          bookingTimeoutMinutes: settings.bookingTimeoutMinutes,
        },
        updatedAt: Timestamp.now(),
      });
      setMessage({ type: 'success', text: 'Business logic saved.' });
    } catch (error: any) {
      console.error('Error saving business logic:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save business logic.' });
    } finally {
      setSaving(false);
    }
  };

  const applyToAllProviders = async () => {
    if (!tenantId) return;

    setApplying(true);
    setMessage(null);
    try {
      const providersSnap = await getDocs(
        query(
          collection(db, 'tenants', tenantId, 'memberships'),
          where('roles', 'array-contains-any', ['owner', 'manager', 'staff', 'coach'])
        )
      );
      const batch = writeBatch(db);
      const now = Timestamp.now();

      providersSnap.docs.forEach((membershipDoc) => {
        const providerId = membershipDoc.id;
        batch.set(
          doc(db, 'providerSchedules', `${tenantId}_${providerId}`),
          {
            tenantId,
            providerId,
            workingPlan: workingPlanWithBreaks,
            timezone,
            createdAt: now,
            updatedAt: now,
          },
          { merge: true }
        );
      });

      batch.update(doc(db, 'tenants', tenantId), {
        businessLogic: {
          workingPlan: workingPlanWithBreaks,
          defaultBreaks: settings.defaultBreaks,
          blockedPeriods: settings.blockedPeriods,
          bookingTimeoutMinutes: settings.bookingTimeoutMinutes,
        },
        updatedAt: now,
      });

      await batch.commit();
      setMessage({ type: 'success', text: 'Business logic applied to all providers.' });
    } catch (error: any) {
      console.error('Error applying business logic:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to apply provider schedules.' });
    } finally {
      setApplying(false);
    }
  };

  return (
    <SettingsShell title="Business Logic">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={4}>
          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontWeight: 700 }}>
              Working Plan
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 760 }}>
              Mark below the days and hours that your company will accept appointments. Staff can still
              adjust appointments outside working hours, but customers cannot book themselves during
              non-working periods. This working plan becomes the default for every new provider record.
            </Typography>

            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessDays.map(({ key, label }) => {
                    const day = settings.workingPlan[key];
                    const enabled = Boolean(day?.isWorking);
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Checkbox
                              checked={enabled}
                              onChange={(event) => updateWorkingDay(key, { isWorking: event.target.checked })}
                            />
                            <Typography>{label}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={day?.start || '09:00'}
                            disabled={!enabled}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              updateWorkingDay(key, { start: event.target.value })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={day?.end || '17:00'}
                            disabled={!enabled}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              updateWorkingDay(key, { end: event.target.value })
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={applying ? <CircularProgress size={18} /> : <DoneIcon />}
                onClick={applyToAllProviders}
                disabled={applying}
              >
                Apply to all providers
              </Button>
            </Box>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontWeight: 700 }}>
              Breaks
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Add the working breaks during each day. These breaks will be applied for all new providers.
            </Typography>
            <Button variant="contained" startIcon={<AddBoxIcon />} onClick={addBreak} sx={{ mb: 2 }}>
              Add Break
            </Button>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.defaultBreaks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary">No breaks configured.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    settings.defaultBreaks.map((breakPeriod) => (
                      <TableRow key={breakPeriod.id}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={breakPeriod.day}
                            onChange={(event) =>
                              updateBreak(breakPeriod.id, { day: event.target.value as BusinessDayKey })
                            }
                          >
                            {businessDays.map((day) => (
                              <MenuItem key={day.key} value={day.key}>
                                {day.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={breakPeriod.start}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              updateBreak(breakPeriod.id, { start: event.target.value })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={breakPeriod.end}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              updateBreak(breakPeriod.id, { end: event.target.value })
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton color="error" onClick={() => removeBreak(breakPeriod.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontWeight: 700 }}>
              Blocked Periods
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Define periods of time where public bookings will be disabled for all providers.
            </Typography>
            <Button variant="contained" startIcon={<SettingsIcon />} onClick={addBlockedPeriod} sx={{ mb: 2 }}>
              Configure
            </Button>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                '.fc': { fontFamily: 'inherit' },
                '.fc .fc-toolbar': { gap: 1, flexWrap: 'wrap' },
                '.fc .fc-button': {
                  bgcolor: 'primary.main',
                  borderColor: 'primary.main',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'primary.dark', borderColor: 'primary.dark' },
                },
                '.fc .fc-button-primary:not(:disabled).fc-button-active': {
                  bgcolor: 'primary.dark',
                  borderColor: 'primary.dark',
                },
                '.fc .fc-event': { borderRadius: 1, cursor: 'pointer' },
              }}
            >
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay',
                }}
                height="auto"
                selectable
                selectMirror
                events={blockedPeriodEvents}
                select={addBlockedPeriodFromSelection}
                eventClick={scrollToBlockedPeriod}
              />
            </Paper>
            <Grid container spacing={2}>
              {settings.blockedPeriods.map((period) => (
                <Grid item xs={12} key={period.id}>
                  <Paper id={`blocked-period-${period.id}`} variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={period.label || ''}
                          onChange={(event) => updateBlockedPeriod(period.id, { label: event.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="datetime-local"
                          label="Start"
                          value={asDateInputValue(period.startTime)}
                          onChange={(event) =>
                            updateBlockedPeriod(period.id, { startTime: fromDateInputValue(event.target.value) })
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="datetime-local"
                          label="End"
                          value={asDateInputValue(period.endTime)}
                          onChange={(event) =>
                            updateBlockedPeriod(period.id, { endTime: fromDateInputValue(event.target.value) })
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton color="error" onClick={() => removeBlockedPeriod(period.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', letterSpacing: 2, fontWeight: 700 }}
            >
              Allow Rescheduling/Cancellation Before
            </Typography>

            <TextField
              sx={{ mt: 2, maxWidth: 220 }}
              fullWidth
              type="number"
              label="Timeout (Minutes)"
              value={settings.bookingTimeoutMinutes}
              onChange={(event) =>
                setSettings((current) => ({
                  ...current,
                  bookingTimeoutMinutes: Math.max(0, parseInt(event.target.value, 10) || 0),
                }))
              }
              inputProps={{ min: 0 }}
            />
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Define the timeout in minutes before customers can book or re-arrange appointments with
              the company.
            </Typography>
          </Box>

          <Box>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? undefined : <SaveIcon />}
              onClick={saveSettings}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Business Logic'}
            </Button>
          </Box>
        </Stack>
      )}
    </SettingsShell>
  );
}
